<template>
  <Teleport to="body">
    <div class="wizard-backdrop" @click.self="$emit('close')">
      <div class="wizard">
        <!-- Header -->
        <div class="wizard-header">
          <div class="wizard-steps">
            <span
              v-for="(label, i) in visibleSteps"
              :key="i"
              class="step-indicator"
              :class="{ active: i === currentIdx, done: i < currentIdx }"
            >
              <span class="step-dot">{{ i < currentIdx ? '✓' : i + 1 }}</span>
              <span class="step-label">{{ label }}</span>
            </span>
          </div>
          <button class="wizard-close" @click="$emit('close')">✕</button>
        </div>

        <!-- ── Step 1: AI CLIENT ─────────────────────────────────────── -->
        <div v-if="step === 'client'" class="wizard-body">
          <h2 class="step-title">Add an AI Client</h2>
          <p class="step-desc">SmartPrompt needs your AI API token to build and run your pipeline. Tokens are encrypted at rest and never shared.</p>

          <div v-if="existingClients.length > 0" class="existing-note">
            <span class="note-label">YOU HAVE {{ existingClients.length }} CLIENT{{ existingClients.length > 1 ? 'S' : '' }}</span>
            <button class="skip-link" @click="step = 'project'">Skip — use existing →</button>
          </div>

          <div class="fields">
            <div class="field-group">
              <label class="field-label">PROVIDER</label>
              <select v-model="clientForm.provider" class="field-input field-select" @change="clientForm.model = modelOptions[0]?.value || ''">
                <option value="anthropic">Anthropic</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">MODEL</label>
              <select v-model="clientForm.model" class="field-input field-select">
                <option v-for="m in modelOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">API TOKEN</label>
              <input v-model="clientForm.token" class="field-input" type="password" :placeholder="tokenPlaceholder" autocomplete="off" />
            </div>
            <div class="field-group">
              <label class="field-label">NAME</label>
              <input v-model="clientForm.name" class="field-input" type="text" placeholder="e.g. My Anthropic Sonnet" />
            </div>
          </div>
          <p v-if="clientError" class="step-error">{{ clientError }}</p>
        </div>

        <!-- ── Step 2: PROJECT ────────────────────────────────────────── -->
        <div v-if="step === 'project'" class="wizard-body">
          <h2 class="step-title">Create Your Project</h2>
          <p class="step-desc">Give your project a clear description — the AI reads this verbatim to build your pipeline, prompts, and feedback loops. The more specific, the better.</p>
          <div class="fields">
            <div class="field-group">
              <label class="field-label">PROJECT NAME</label>
              <input v-model="projectForm.name" class="field-input" type="text" placeholder="e.g. Orion Matcher" />
            </div>
            <div class="field-group">
              <label class="field-label">DESCRIPTION <span class="label-hint">— be specific, this drives everything</span></label>
              <textarea v-model="projectForm.description" class="field-input field-textarea" rows="5" placeholder="Match supermarket product names to items in our database and extract attributes like organic, fat percentage, volume and brand. Categories must match our existing category tree." />
            </div>
          </div>
          <p v-if="projectError" class="step-error">{{ projectError }}</p>
        </div>

        <!-- ── Step 3: ASSIGN CLIENTS ─────────────────────────────────── -->
        <div v-if="step === 'assign'" class="wizard-body">
          <h2 class="step-title">Assign AI Clients</h2>
          <p class="step-desc">Each purpose uses a different client. Use a cheaper model for critique to save costs at volume.</p>
          <div class="fields">
            <div v-for="purpose in ['main', 'critique', 'orchestrator']" :key="purpose" class="field-group">
              <label class="field-label">
                {{ purpose.toUpperCase() }}
                <span class="label-hint purpose-hint">{{ purposeHint[purpose] }}</span>
              </label>
              <select v-model="assignForm[purpose]" class="field-input field-select">
                <option value="">— Select client —</option>
                <option v-for="c in allClients" :key="c.id" :value="c.id">{{ c.name }} — {{ c.model }}</option>
              </select>
            </div>
          </div>
          <p v-if="assignError" class="step-error">{{ assignError }}</p>
        </div>

        <!-- ── Step 4: API KEY ────────────────────────────────────────── -->
        <div v-if="step === 'apikey'" class="wizard-body">
          <h2 class="step-title">{{ newApiKey ? 'Copy Your API Key' : 'Create an API Key' }}</h2>
          <p v-if="!newApiKey" class="step-desc">Your application calls SmartPrompt using this key. It identifies both you and this project. The full key is only shown once.</p>

          <template v-if="!newApiKey">
            <div class="fields">
              <div class="field-group">
                <label class="field-label">KEY NAME</label>
                <input v-model="apiKeyForm.name" class="field-input" type="text" placeholder="e.g. Production server" />
              </div>
            </div>
            <p v-if="apiKeyError" class="step-error">{{ apiKeyError }}</p>
          </template>

          <template v-else>
            <p class="key-warning">⚠ Copy this now — it will never be shown again.</p>
            <div class="key-reveal">
              <span class="key-text">{{ newApiKey }}</span>
              <button class="copy-btn" @click="copyApiKey">{{ copied ? 'COPIED ✓' : 'COPY' }}</button>
            </div>
            <p class="key-hint">Use as: <code>Authorization: Bearer {{ newApiKey.slice(0, 20) }}…</code></p>
          </template>
        </div>

        <!-- Footer -->
        <div class="wizard-footer">
          <button v-if="canGoBack" class="btn-back" :disabled="saving" @click="goBack">← BACK</button>
          <div class="footer-right">
            <span v-if="saving" class="saving-text">{{ savingLabel }}…</span>
            <button
              v-if="step !== 'apikey' || !newApiKey"
              class="btn-next"
              :disabled="saving || !canAdvance"
              @click="advance"
            >
              {{ nextLabel }}
            </button>
            <button v-else class="btn-next" @click="finish">OPEN PROJECT →</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { router } from "@inertiajs/vue3"

const emit = defineEmits(["close"])

// ── State ────────────────────────────────────────────────────────────────────
const step          = ref("client")   // client | project | assign | apikey
const saving        = ref(false)
const existingClients = ref([])

// Created during wizard:
const createdClientId  = ref(null)
const createdProjectUuid = ref(null)

// Forms
const clientForm  = ref({ provider: "anthropic", model: "claude-sonnet-4-6", token: "", name: "" })
const projectForm = ref({ name: "", description: "" })
const assignForm  = ref({ main: "", critique: "", orchestrator: "" })
const apiKeyForm  = ref({ name: "Production server" })

// Errors
const clientError  = ref("")
const projectError = ref("")
const assignError  = ref("")
const apiKeyError  = ref("")

// API key reveal
const newApiKey = ref("")
const copied    = ref(false)

// ── Computed ──────────────────────────────────────────────────────────────────
const allClients = computed(() => {
  const created = createdClientId.value
    ? existingClients.value.find((c) => c.id === createdClientId.value) ? [] : [{ id: createdClientId.value, name: clientForm.value.name, model: clientForm.value.model }]
    : []
  return [...created, ...existingClients.value]
})

const modelOptions = computed(() => {
  const map = {
    anthropic: [
      { value: "claude-sonnet-4-6",      label: "claude-sonnet-4-6 (recommended)" },
      { value: "claude-opus-4-6",        label: "claude-opus-4-6 (most capable)" },
      { value: "claude-haiku-4-5-20251001", label: "claude-haiku-4-5 (fast + cheap)" },
    ],
    openai: [
      { value: "gpt-4o",       label: "gpt-4o (recommended)" },
      { value: "gpt-4o-mini",  label: "gpt-4o-mini (cheap)" },
      { value: "gpt-4-turbo",  label: "gpt-4-turbo" },
    ],
    google: [
      { value: "gemini-2.0-flash", label: "gemini-2.0-flash (recommended)" },
      { value: "gemini-1.5-pro",   label: "gemini-1.5-pro" },
      { value: "gemini-1.5-flash", label: "gemini-1.5-flash (fast)" },
    ],
  }
  return map[clientForm.value.provider] ?? []
})

const tokenPlaceholder = computed(() => {
  const map = { anthropic: "sk-ant-api03-…", openai: "sk-proj-…", google: "AIza…" }
  return map[clientForm.value.provider] ?? "token…"
})

const purposeHint = {
  main:         "runs your pipeline steps",
  critique:     "reviews each step's output (use cheaper model)",
  orchestrator: "analyzes failures and rewrites prompts",
}

const visibleSteps = ["AI CLIENT", "PROJECT", "ASSIGN CLIENTS", "API KEY"]
const stepOrder    = ["client", "project", "assign", "apikey"]
const currentIdx   = computed(() => stepOrder.indexOf(step.value))
const canGoBack    = computed(() => currentIdx.value > 0 && step.value !== "apikey")

const canAdvance = computed(() => {
  if (step.value === "client")  return !!(clientForm.value.token && clientForm.value.name) || existingClients.value.length > 0
  if (step.value === "project") return !!(projectForm.value.name.trim() && projectForm.value.description.trim())
  if (step.value === "assign")  return !!(assignForm.value.main)
  if (step.value === "apikey")  return !!apiKeyForm.value.name.trim()
  return false
})

const nextLabel = computed(() => {
  if (saving.value) return "…"
  if (step.value === "client")  return "NEXT →"
  if (step.value === "project") return "NEXT →"
  if (step.value === "assign")  return "ASSIGN & CONTINUE →"
  if (step.value === "apikey")  return "CREATE KEY"
  return "NEXT"
})

const savingLabel = computed(() => {
  if (step.value === "client")  return "Creating client"
  if (step.value === "project") return "Creating project"
  if (step.value === "assign")  return "Assigning clients"
  if (step.value === "apikey")  return "Creating key"
  return "Saving"
})

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const res  = await apiGet("/api/v1/ai_clients")
    const data = await res.json()
    existingClients.value = data.ai_clients ?? []
    // Pre-fill assign dropdowns if clients already exist
    if (existingClients.value.length > 0) {
      const first = existingClients.value[0].id
      assignForm.value = { main: first, critique: first, orchestrator: first }
    }
  } catch { /* ignore */ }
})

// ── Helpers ───────────────────────────────────────────────────────────────────
function apiGet(path) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  return fetch(path, { headers: { "X-CSRF-Token": token } })
}
async function apiPost(path, body) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  const res = await fetch(path, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body:    JSON.stringify(body),
  })
  return { status: res.status, data: await res.json() }
}

function goBack() {
  const prev = { project: "client", assign: "project", apikey: "assign" }
  step.value = prev[step.value] ?? "client"
}

async function advance() {
  if (step.value === "client")  await saveClient()
  else if (step.value === "project") await saveProject()
  else if (step.value === "assign")  await saveAssign()
  else if (step.value === "apikey")  await saveApiKey()
}

// ── Step handlers ─────────────────────────────────────────────────────────────
async function saveClient() {
  // If user has clients and hasn't filled form, just skip
  if (existingClients.value.length > 0 && !clientForm.value.token) {
    step.value = "project"
    return
  }
  clientError.value = ""
  saving.value      = true
  try {
    const { status, data } = await apiPost("/api/v1/ai_clients", {
      provider: clientForm.value.provider,
      model:    clientForm.value.model,
      token:    clientForm.value.token,
      name:     clientForm.value.name,
    })
    if (status === 201) {
      createdClientId.value = data.ai_client?.id
      existingClients.value = [...existingClients.value, data.ai_client]
      const id = data.ai_client?.id
      assignForm.value = { main: id, critique: id, orchestrator: id }
      step.value = "project"
    } else {
      clientError.value = data.message || "Failed to create client."
    }
  } catch { clientError.value = "Network error." }
  finally { saving.value = false }
}

async function saveProject() {
  projectError.value = ""
  saving.value       = true
  try {
    const { status, data } = await apiPost("/api/v1/projects", {
      name:        projectForm.value.name,
      description: projectForm.value.description,
      rag_adapter: "pgvector",
    })
    if (status === 201) {
      createdProjectUuid.value = data.project?.uuid
      step.value = "assign"
    } else {
      projectError.value = data.message || "Failed to create project."
    }
  } catch { projectError.value = "Network error." }
  finally { saving.value = false }
}

async function saveAssign() {
  assignError.value = ""
  saving.value      = true
  const uuid = createdProjectUuid.value
  try {
    const purposes = [
      { purpose: "main",         ai_client_id: assignForm.value.main,         is_default: true },
      { purpose: "critique",     ai_client_id: assignForm.value.critique,     is_default: true },
      { purpose: "orchestrator", ai_client_id: assignForm.value.orchestrator, is_default: true },
    ].filter((p) => p.ai_client_id)

    const results = await Promise.all(
      purposes.map((p) => apiPost(`/api/v1/projects/${uuid}/ai_clients`, p))
    )
    const failed = results.find((r) => r.status !== 201)
    if (failed) { assignError.value = failed.data?.message || "Failed to assign client."; return }
    step.value = "apikey"
  } catch { assignError.value = "Network error." }
  finally { saving.value = false }
}

async function saveApiKey() {
  apiKeyError.value = ""
  saving.value      = true
  const uuid = createdProjectUuid.value
  try {
    const { status, data } = await apiPost(`/api/v1/projects/${uuid}/api_keys`, {
      name: apiKeyForm.value.name,
    })
    if (status === 201) {
      newApiKey.value = data.api_key?.key || ""
    } else {
      apiKeyError.value = data.message || "Failed to create key."
    }
  } catch { apiKeyError.value = "Network error." }
  finally { saving.value = false }
}

async function copyApiKey() {
  try { await navigator.clipboard.writeText(newApiKey.value); copied.value = true } catch {}
}

function finish() {
  router.visit(`/projects/${createdProjectUuid.value}`)
  emit("close")
}
</script>

<style scoped>
.wizard-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.wizard {
  background: #111113;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 16px;
  flex-shrink: 0;
}

.wizard-steps {
  display: flex;
  gap: 14px;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  opacity: 0.3;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.step-indicator.active { opacity: 1; }
.step-indicator.done   { opacity: 0.6; }

.step-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(245, 166, 35, 0.15);
  border: 1px solid rgba(245, 166, 35, 0.3);
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  color: #f5a623;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-indicator.active .step-dot {
  background: #f5a623;
  color: #0a0a0b;
}

.step-indicator.done .step-dot {
  background: rgba(74, 222, 128, 0.2);
  border-color: rgba(74, 222, 128, 0.4);
  color: #4ade80;
}

.step-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: rgba(232, 230, 227, 0.6);
  white-space: nowrap;
}

.wizard-close {
  background: none;
  border: none;
  color: rgba(232, 230, 227, 0.3);
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s;
  flex-shrink: 0;
}
.wizard-close:hover { color: #e8e6e3; }

.wizard-body {
  padding: 24px 22px;
  overflow-y: auto;
  flex: 1;
}

.step-title {
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 6px;
}

.step-desc {
  font-family: var(--font-body);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.5);
  margin: 0 0 20px;
  line-height: 1.6;
}

.existing-note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(74, 222, 128, 0.08);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 5px;
  margin-bottom: 16px;
}

.note-label {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #4ade80;
}

.skip-link {
  background: none;
  border: none;
  font-family: var(--font-mono);
  font-size: 10px;
  color: #4ade80;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.fields { display: flex; flex-direction: column; gap: 14px; }

.field-group { display: flex; flex-direction: column; gap: 5px; }

.field-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.35);
}

.label-hint {
  font-weight: 400;
  letter-spacing: 0;
  color: rgba(232, 230, 227, 0.2);
  font-size: 9px;
}

.purpose-hint { display: block; margin-top: 1px; }

.field-input {
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  padding: 9px 12px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
  outline: none;
  transition: border-color 0.15s;
}
.field-input:focus { border-color: rgba(245, 166, 35, 0.45); }
.field-input::placeholder { color: rgba(232, 230, 227, 0.18); }

.field-textarea { resize: vertical; min-height: 100px; line-height: 1.5; }
.field-select   { appearance: none; cursor: pointer; }

.step-error {
  font-family: var(--font-mono);
  font-size: 10px;
  color: #f87171;
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(248, 113, 113, 0.08);
  border: 1px solid rgba(248, 113, 113, 0.2);
  border-radius: 4px;
}

/* Key reveal */
.key-warning {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f5a623;
  margin-bottom: 14px;
}

.key-reveal {
  display: flex;
  border: 1px solid rgba(245, 166, 35, 0.3);
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 10px;
}

.key-text {
  flex: 1;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: #e8e6e3;
  word-break: break-all;
  background: rgba(245, 166, 35, 0.05);
}

.copy-btn {
  padding: 10px 14px;
  background: rgba(245, 166, 35, 0.15);
  border: none;
  border-left: 1px solid rgba(245, 166, 35, 0.3);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: #f5a623;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.copy-btn:hover { background: rgba(245, 166, 35, 0.25); }

.key-hint {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}
.key-hint code { color: rgba(232, 230, 227, 0.45); }

/* Footer */
.wizard-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.saving-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.35);
}

.btn-back {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  color: rgba(232, 230, 227, 0.4);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.btn-back:hover:not(:disabled) { color: #e8e6e3; border-color: rgba(255, 255, 255, 0.15); }
.btn-back:disabled { opacity: 0.3; cursor: not-allowed; }

.btn-next {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 9px 20px;
  border-radius: 4px;
  border: none;
  background: #f5a623;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.15s;
}
.btn-next:hover:not(:disabled) { opacity: 0.85; }
.btn-next:disabled { opacity: 0.35; cursor: not-allowed; }
</style>
